import { TerrainGrid } from '../TerrainGrid';
import { PheromoneGrid } from '../PheromoneGrid';
import { ParkingtonSurfaceAntController } from '../ParkingtonSurfaceAnts';
import { Portal, TransferableEntity, PortalBuilder } from '@antification/protocol';
import { Rng } from '../../Rng';
import { AntExportData } from '../../colony/Ant';

export const COLONY_COUNT = 3;
export const MAX_ENTRANCES_PER_COLONY = 3;

/**
 * Where each colony's entrance sits, given a generator.
 *
 * One portal location per colony, spread across the map in equal sections. Both
 * the layer and a loaded save need this, and both have to agree — a portal that
 * moved on load would leave every returning forager walking to the wrong hole.
 */
export function pickPortalCells(
  rng: Rng,
  cols: number,
  rows: number,
  colonyCount = COLONY_COUNT
): Array<{ x: number; y: number }> {
  const cells: Array<{ x: number; y: number }> = [];
  for (let i = 0; i < colonyCount; i++) {
    const sectionWidth = Math.floor((cols - 6) / colonyCount);
    const baseX = 3 + i * sectionWidth;
    cells.push({
      x: baseX + rng.int(Math.min(sectionWidth, cols - 6 - baseX)),
      y: 3 + rng.int(rows - 6),
    });
  }
  return cells;
}

/**
 * Callbacks for portal operations that need orchestrator access.
 */
export interface PortalCallbacks {
  registerPortal(portal: Portal): void;
  getPortals(): Portal[];
}

/**
 * Manages portal setup, entity receiving, and position updates.
 */
export class PortalSystem {
  constructor(
    private grid: TerrainGrid,
    private pheromones: PheromoneGrid,
    private surfaceAntController: ParkingtonSurfaceAntController,
    private rng: Rng,
  ) {}

  /**
   * Set up portals that connect to the colony layers.
   * Creates one portal location per colony, spread across the map.
   */
  setupPortals(
    portalCells: Array<{ x: number; y: number }>,
    callbacks: PortalCallbacks,
  ): void {
    portalCells.length = 0;
    for (const cell of pickPortalCells(this.rng, this.grid.getCols(), this.grid.getRows())) {
      portalCells.push(cell);
    }

    for (let colonyIndex = 0; colonyIndex < COLONY_COUNT; colonyIndex++) {
      const portalCell = portalCells[colonyIndex];

      for (let entranceIndex = 1; entranceIndex <= MAX_ENTRANCES_PER_COLONY; entranceIndex++) {
        const entrance = PortalBuilder.create(`surface-entrance-colony-${colonyIndex}-${entranceIndex}`)
          .inLayer('parkington')
          .connectsTo(`formicarium-${colonyIndex}`, `colony-${colonyIndex}-exit-${entranceIndex}`)
          .at(portalCell.x, portalCell.y)
          .ofType('entrance')
          .allowsEntities('ant')
          .withRadius(20)
          .build();

        callbacks.registerPortal(entrance);
      }
    }

    this.pheromones.initializeNestScent(portalCells);
  }

  /**
   * Receive an entity transferred from another layer.
   *
   * Returns whether this layer took it. A surface entrance under a federal slab
   * refuses (Q27) — see `ParkingtonSurfaceAntController.entranceIsSealed` — and an
   * entity type nobody here handles is refused for the older reason that it was
   * never going to arrive.
   */
  receiveEntity(entity: TransferableEntity, fromPortal: string): boolean {
    if (entity.type === 'ant') {
      return this.handleAntArrival(entity.data as AntExportData, fromPortal);
    }
    return false;
  }

  /**
   * Handle an ant arriving from the colony.
   */
  private handleAntArrival(antData: AntExportData, portalId: string): boolean {
    return this.surfaceAntController.handleAntArrival(antData, portalId);
  }

  /**
   * Move each colony's entrances onto the cell that colony's entrance is in.
   *
   * Only a restore needs this: `setupPortals` builds the portals from the cells
   * it has just picked, and nothing moves an entrance afterwards, but a loaded
   * save replaces the cells under portals that were built for a different
   * district. It used to be called on window resize too, back when a portal's
   * position was a pixel and therefore something a resize could invalidate.
   */
  syncPortalPositions(
    portalCells: ReadonlyArray<{ x: number; y: number }>,
    portals: Portal[],
  ): void {
    for (let colonyIndex = 0; colonyIndex < portalCells.length; colonyIndex++) {
      const portalCell = portalCells[colonyIndex];
      if (!portalCell) continue;

      for (const portal of portals) {
        if (portal.id.includes(`colony-${colonyIndex}`)) {
          portal.position.x = portalCell.x;
          portal.position.y = portalCell.y;
        }
      }
    }
  }
}
